import { Close, LinkOff, Search } from "@mui/icons-material"
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  IconButton,
  Paper,
  Snackbar,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material"
import { t } from "i18next"
import Joi from "joi"
import { noop } from "lodash-es"
import React, { FC, useCallback, useMemo } from "react"
import { useNavigate } from "react-router-dom"
import { Locker } from "shared/locker"
import { LockerThingTypeMrn } from "shared/lockerData"
import { SharedAccessPatternSaEnum } from "shared/sharedAccessPatternsData"
import { SstLockerThing } from "src/features/partners/sstThingsSlice"
import {
  useCreateThingsMutation,
  useGetThingsQuery,
  useUpdateThingsMutation,
} from "../features/things/thingsApi"
import FormFields, { FieldsGeneratorFn, FormField } from "./FormFields"
import { buildV2ModuleOrder } from "./lockerModuleOrder"
import LockerModuleOrderMapping, {
  MappableModule,
} from "./LockerModuleOrderMapping"
import SaLockerSelectorDialog from "./SaLockerSelectorDialog"
import { Sl2FormContainer } from "./Sl2FormContainer"
import { SstLockerDisplay } from "./SstLockerDisplay"

// ── Defaults & Validation ─────────────────────────────────

const defaultLocker: Partial<Locker> = {
  thingTypeMrn: LockerThingTypeMrn,
}

const schema = Joi.object({
  name: Joi.string().allow("").max(100).optional(),
  nameEn: Joi.string().allow("").max(100).optional(),
  nameFr: Joi.string().allow("").max(100).optional(),
  description: Joi.string().allow("").max(10000).optional(),
  address1: Joi.string().allow("").max(1000).optional(),
  address2: Joi.string().allow("").max(1000).optional(),
  city: Joi.string().allow("").max(100).optional(),
  province: Joi.string().allow("").max(100).optional(),
  country: Joi.string().allow("").max(100).optional(),
  postalCode: Joi.string().allow("").max(100).optional(),
  latitude: Joi.string().allow("").max(100).optional(),
  longitude: Joi.string().allow("").max(100).optional(),
  lockerLocation: Joi.string().allow("").max(1000).optional(),
  lockerLocationEn: Joi.string().allow("").max(1000).optional(),
  lockerLocationFr: Joi.string().allow("").max(1000).optional(),
  displayOrder: Joi.number().allow("").optional(),
  isVirtual: Joi.boolean().optional(),
  tenantId: Joi.string().allow("").optional(),
  sstLockerId: Joi.string().allow("").optional(),
  sstLocker: Joi.object().optional(),
})

// ── Section Header ────────────────────────────────────────

const SectionHeader: FC<{ title: string; subtitle?: string }> = ({
  title,
  subtitle,
}) => (
  <Box sx={{ mb: 1.5 }}>
    <Typography variant="subtitle2" fontWeight={600} color="text.secondary">
      {title}
    </Typography>
    {subtitle && (
      <Typography variant="caption" color="text.secondary">
        {subtitle}
      </Typography>
    )}
  </Box>
)

// ── Field Definitions ─────────────────────────────────────

const fieldsGeneralFn: FieldsGeneratorFn = (action) => {
  const fields = [
    {
      name: "name",
      fieldType: "text",
      label: t("name"),
      required: true,
      size: "small",
      fullWidth: true,
    },
    {
      name: "description",
      fieldType: "text",
      label: t("description"),
      required: false,
      size: "small",
      fullWidth: true,
      InputProps: { multiline: true, rows: 3 },
    },
    {
      name: "address1",
      fieldType: "text",
      label: t("address1"),
      required: false,
      size: "small",
      fullWidth: true,
    },
    {
      name: "address2",
      fieldType: "text",
      label: t("address2"),
      required: false,
      size: "small",
      fullWidth: true,
    },
    {
      name: "city",
      fieldType: "text",
      label: t("city"),
      required: false,
      size: "small",
      fullWidth: true,
      gridProps: { xs: 12, sm: 6 },
    },
    {
      name: "province",
      fieldType: "text",
      label: t("province"),
      required: false,
      size: "small",
      fullWidth: true,
      gridProps: { xs: 12, sm: 6 },
    },
    {
      name: "country",
      fieldType: "text",
      label: t("country"),
      required: false,
      size: "small",
      fullWidth: true,
      gridProps: { xs: 12, sm: 6 },
    },
    {
      name: "postalCode",
      fieldType: "text",
      label: t("postalCode"),
      required: false,
      size: "small",
      fullWidth: true,
      gridProps: { xs: 12, sm: 6 },
    },
    {
      name: "latitude",
      fieldType: "text",
      label: t("latitude"),
      required: false,
      size: "small",
      fullWidth: true,
      gridProps: { xs: 12, sm: 6 },
    },
    {
      name: "longitude",
      fieldType: "text",
      label: t("longitude"),
      required: false,
      size: "small",
      fullWidth: true,
      gridProps: { xs: 12, sm: 6 },
    },
    {
      name: "lockerLocation",
      fieldType: "text",
      label: t("lockerLocation"),
      required: false,
      size: "small",
      fullWidth: true,
    },
    {
      name: "displayOrder",
      fieldType: "number",
      type: "number",
      label: t("displayOrder"),
      required: false,
      size: "small",
      fullWidth: true,
    },
  ]
  if (action === "create") {
    return [
      {
        name: "isVirtual",
        fieldType: "bool",
        label: "virtual",
        required: false,
        size: "small",
        fullWidth: true,
      },
      ...fields,
    ] as FormField[]
  }
  return fields as FormField[]
}

const fieldsNotifFn: FieldsGeneratorFn = () => {
  return [
    {
      name: "nameEn",
      fieldType: "text",
      label: t("nameEn"),
      required: false,
      size: "small",
      fullWidth: true,
      gridProps: { xs: 12, sm: 6 },
    },
    {
      name: "nameFr",
      fieldType: "text",
      label: t("nameFr"),
      required: false,
      size: "small",
      fullWidth: true,
      gridProps: { xs: 12, sm: 6 },
    },
    {
      name: "lockerLocationEn",
      fieldType: "text",
      label: t("lockerLocationEn"),
      required: false,
      size: "small",
      fullWidth: true,
      gridProps: { xs: 12, sm: 6 },
    },
    {
      name: "lockerLocationFr",
      fieldType: "text",
      label: t("lockerLocationFr"),
      required: false,
      size: "small",
      fullWidth: true,
      gridProps: { xs: 12, sm: 6 },
    },
  ] as FormField[]
}

// ── Main Component ────────────────────────────────────────

export const SaLockerForm: FC<{
  locker?: Locker
  isCreating?: boolean
  onFinish?: (isSuccess: boolean) => void
  onCancel?: () => void
}> = ({
  locker = defaultLocker,
  isCreating = false,
  onFinish = noop,
  onCancel = noop,
}) => {
  const [snackbarData, setSnackbarData] = React.useState({
    severity: "success" as "success" | "error" | "info" | "warning",
    message: "",
  })

  const [updateThings, { isLoading: isUpdatingThings }] =
    useUpdateThingsMutation()
  const [createThings, { isLoading: isCreatingThings }] =
    useCreateThingsMutation()
  const isSaving = isUpdatingThings || isCreatingThings

  const navigate = useNavigate()
  const [confirmDialogOpen, setConfirmDialogOpen] = React.useState(false)
  const [selectedLocker, setSelectedLocker] =
    React.useState<SstLockerThing | null>(null)
  const [dialogOpen, setDialogOpen] = React.useState(false)
  const [sstApiVersion, setSstApiVersion] = React.useState<"v1" | "v2">(
    (locker as any).sstApiVersion || "v1",
  )
  const [isVirtualLocal, setIsVirtualLocal] = React.useState(locker.isVirtual)

  const handleLockerSelect = (locker: SstLockerThing) => {
    setSelectedLocker(locker)
    setDialogOpen(false)
  }

  const onSubmit = useCallback(
    async (data: any) => {
      try {
        setConfirmDialogOpen(false)
        const mutateFn = isCreating ? createThings : updateThings
        const thing = {
          ...defaultLocker,
          ...data,
          id: locker.id,
          thingTypeMrn: LockerThingTypeMrn,
          tenantId: isCreating ? data.tenantId : locker?.tenantId,
        }
        if (isCreating) {
          thing.lockerProvider = "sst"
          thing.lockerType = "sst.pharma"
          thing.sstApiVersion = sstApiVersion
        }
        if (selectedLocker?._id) {
          thing.sstLockerId = selectedLocker._id
          if (sstApiVersion !== "v2") {
            thing.sstLocker = selectedLocker
          }
          // V2: backend fetches sst2Locker from SST V2 API in preCreate
        }

        // Clean up empty objects from form defaults
        if (!thing.sstLocker || Object.keys(thing.sstLocker).length === 0) {
          delete thing.sstLocker
        }

        const res = await mutateFn({
          body: { things: [thing] },
          params: { tenantId: isCreating ? data.tenantId : locker?.tenantId },
        }).unwrap()

        setSnackbarData({
          severity: "success",
          message: isCreating
            ? "Locker created successfully"
            : "Locker updated successfully",
        })
        onFinish(true)
        if (isCreating) {
          navigate(`/sa/lockers/${res.things[0].id}`)
        }
      } catch (error: any) {
        console.error(error)
        setSnackbarData({
          severity: "error",
          message: error?.message || error?.data?.error || "An error occurred",
        })
        onFinish(false)
      }
    },
    [
      createThings,
      isCreating,
      locker.id,
      locker?.tenantId,
      navigate,
      onFinish,
      selectedLocker,
      sstApiVersion,
      updateThings,
    ],
  )

  // ── Tenant options ────────────────────────────────────
  const { data } = useGetThingsQuery({
    params: {
      "ap.name": SharedAccessPatternSaEnum.SaGetTenants,
      "ap.scanIndexForward": false,
      "ap.tenantOnly": true,
      "ap.limit": 1000,
      "ap.nextToken": undefined,
    },
  })

  const tenantOptions = useMemo(() => {
    return (data?.things || []).map((tenant) => ({
      id: tenant.id,
      label: (
        <Stack direction="row" spacing={2} alignItems="center" sx={{ py: 0.5 }}>
          <Typography variant="body2" fontWeight={600}>
            {tenant.name}
          </Typography>
          {tenant.mblsProviderId && (
            <Chip
              label={`ID ${tenant.mblsProviderId}`}
              size="small"
              color="primary"
              variant="outlined"
              sx={{ height: 20, fontSize: "0.7rem" }}
            />
          )}
          <Typography variant="caption" color="text.secondary">
            {tenant.id}
          </Typography>
        </Stack>
      ),
    }))
  }, [data?.things])

  // ── Fields ────────────────────────────────────────────
  const { fieldsGeneral, defaultValues, fieldsNotif } = useMemo(() => {
    const action = isCreating ? "create" : "update"
    let fieldsGeneral = fieldsGeneralFn(action)
    let fieldsNotif = fieldsNotifFn(action)

    const defaultValues = {} as Record<string, any>
    ;[...fieldsGeneral, ...fieldsNotif].forEach(({ fieldType, name }) => {
      if (fieldType === "bool") {
        // @ts-ignore
        defaultValues[name] = locker[name] || false
        return
      }
      // @ts-ignore
      defaultValues[name] = (locker[name] as any) || ""
    })

    if (isCreating) {
      const noEmptyOptions = [{ id: "", label: "No options" }]
      fieldsGeneral = [
        {
          name: "tenantId",
          fieldType: "select",
          options: tenantOptions.length ? tenantOptions : noEmptyOptions,
          label: t("tenant"),
          placeholder: t("selectUser"),
          required: true,
          size: "small",
          fullWidth: true,
          disabled: false,
          valueKey: "id",
        } as FormField,
        ...fieldsGeneral,
      ]
      const [tenantField, isVirtualField, ...rest] = fieldsGeneral
      if (!isVirtualLocal) {
        fieldsGeneral = [tenantField, isVirtualField, ...rest]
      }
    }

    return { fieldsGeneral, fieldsNotif, defaultValues }
  }, [isCreating, isVirtualLocal, locker, tenantOptions])

  // Modules the order editor can position. A V1 locker carries them as
  // `sstLocker.locker_grid`; a V2 locker has no `locker_grid` at all — `:312`
  // deliberately never stores `sstLocker` for v2 — and carries
  // `sst2Locker.templates_association` instead (LOCKER-390). Read the
  // PERSISTED `locker.sstApiVersion`, not the `sstApiVersion` form state: this
  // section is edit-only and describes the locker as saved, not an unsaved
  // toggle.
  const mappableModules: MappableModule[] = useMemo(() => {
    if (locker?.sstApiVersion === "v2") {
      return buildV2ModuleOrder(locker?.sst2Locker).map(({ tmpl }) => ({
        name: tmpl.name || tmpl.template?.name,
      }))
    }
    return locker?.sstLocker?.locker_grid || []
  }, [locker?.sstApiVersion, locker?.sst2Locker, locker?.sstLocker?.locker_grid])

  const nbGrid = mappableModules.length

  // ── Render ────────────────────────────────────────────
  return (
    <Paper
      elevation={0}
      sx={{
        // In edit mode the console page already frames and titles this form
        // (ConsoleSection + the graphite header), so a second border, a second
        // max-width and a repeated locker name would all be chrome about chrome.
        p: isCreating ? { xs: 2, sm: 4 } : 0,
        maxWidth: isCreating ? 860 : "none",
        mx: isCreating ? "auto" : 0,
        border: isCreating ? "1px solid" : "none",
        borderColor: "divider",
        borderRadius: 2,
        bgcolor: "transparent",
      }}
    >
      {isCreating && (
        <>
          <Stack direction="row" alignItems="center" sx={{ mb: 3 }}>
            <Typography variant="h6" fontWeight={700}>
              {t("newLocker")}
            </Typography>
          </Stack>
          <Divider sx={{ mb: 3 }} />
        </>
      )}

      <Sl2FormContainer
        joiSchema={schema}
        onSuccess={onSubmit}
        defaultValues={defaultValues}
      >
        <Stack direction="column" spacing={4}>
          {/* ── Section: SST Integration (create only, non-virtual) ── */}
          <Collapse in={isCreating && !isVirtualLocal}>
            <Card variant="outlined" sx={{ bgcolor: "grey.50" }}>
              <CardContent>
                <SectionHeader
                  title={t("sstApiVersion")}
                  subtitle="Select which SirSteward API version to use for this locker"
                />

                <ToggleButtonGroup
                  value={sstApiVersion}
                  exclusive
                  onChange={(_e, val) => {
                    if (val) {
                      setSstApiVersion(val)
                      setSelectedLocker(null)
                    }
                  }}
                  size="small"
                  sx={{ mb: 2 }}
                >
                  <ToggleButton
                    value="v1"
                    sx={{ textTransform: "none", px: 3 }}
                  >
                    SST V1
                  </ToggleButton>
                  <ToggleButton
                    value="v2"
                    sx={{ textTransform: "none", px: 3 }}
                  >
                    SST V2 (Mobilus API)
                  </ToggleButton>
                </ToggleButtonGroup>

                <Divider sx={{ my: 2 }} />

                <SectionHeader title={t("sstLockerAssociated")} />

                {!selectedLocker ? (
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<Search />}
                    onClick={() => setDialogOpen(true)}
                    sx={{ textTransform: "none" }}
                  >
                    {t("selectLocker")}
                  </Button>
                ) : (
                  <Stack direction="column" spacing={1}>
                    <SstLockerDisplay locker={selectedLocker} />
                    <Stack direction="row" justifyContent="flex-end">
                      <Button
                        size="small"
                        color="inherit"
                        startIcon={<LinkOff />}
                        onClick={() => setSelectedLocker(null)}
                        sx={{ textTransform: "none", color: "text.secondary" }}
                      >
                        {t("clear")}
                      </Button>
                    </Stack>
                  </Stack>
                )}
              </CardContent>
            </Card>
          </Collapse>

          <SaLockerSelectorDialog
            open={dialogOpen}
            onClose={() => setDialogOpen(false)}
            onSelect={handleLockerSelect}
            sstApiVersion={sstApiVersion}
          />

          {/* ── Section: General Information ── */}
          <Box>
            <SectionHeader
              title={t("general")}
              subtitle={isCreating ? "Required fields are marked" : undefined}
            />
            <FormFields
              fields={fieldsGeneral}
              onWatch={(value, { name }) => {
                if (name === "isVirtual") {
                  setIsVirtualLocal(value?.isVirtual)
                }
              }}
            />
          </Box>

          {/* ── Section: Localization / i18n ── */}
          <Box>
            <SectionHeader title={t("i18nFields")} />
            <Alert severity="info" variant="outlined" sx={{ mb: 2, fontSize: "0.8rem" }}>
              {t("i18nFieldsDescription")}
            </Alert>
            <FormFields fields={fieldsNotif} />
          </Box>

          {/* ── Section: Module Mapping (edit only) ── */}
          {!isCreating && (
            <Box>
              <SectionHeader title={t("lockerModuleOrderMapping.title")} />
              {nbGrid > 1 ? (
                <LockerModuleOrderMapping
                  // Remount when the module list changes shape — the editor
                  // seeds its local rows from props once, so a locker whose
                  // snapshot arrives (or changes module count) after mount
                  // would otherwise keep editing the stale shape.
                  key={`${locker.id}-${nbGrid}`}
                  moduleMapping={locker.moduleMapping || []}
                  locker={locker}
                  modules={mappableModules}
                />
              ) : nbGrid === 1 ? (
                <Alert severity="info" variant="outlined" sx={{ fontSize: "0.8rem" }}>
                  {t("lockerModuleOrderMapping.warningSingleItem")}
                </Alert>
              ) : (
                <Alert severity="warning" variant="outlined" sx={{ fontSize: "0.8rem" }}>
                  {t("lockerModuleOrderMapping.noGrid")}
                </Alert>
              )}
            </Box>
          )}

          {/* ── Actions ── */}
          <Divider />
          <Stack
            direction="row"
            spacing={2}
            justifyContent="flex-end"
          >
            <Button
              variant="text"
              size="small"
              onClick={onCancel}
              sx={{ textTransform: "none", minWidth: 100 }}
              disabled={isSaving}
            >
              {t("cancel")}
            </Button>
            <Button
              variant="contained"
              size="small"
              disableElevation
              type="submit"
              sx={{ textTransform: "none", minWidth: 140 }}
              disabled={isSaving}
              startIcon={
                isSaving ? <CircularProgress size={16} color="inherit" /> : null
              }
            >
              {isSaving
                ? t("saving")
                : t(isCreating ? "create" : "save")}
            </Button>
          </Stack>
        </Stack>
      </Sl2FormContainer>

      {/* ── Snackbar ── */}
      <Snackbar
        open={snackbarData.message !== ""}
        onClose={() =>
          setSnackbarData({ severity: "success", message: "" })
        }
        autoHideDuration={4000}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={snackbarData.severity}
          variant="filled"
          action={
            <IconButton
              size="small"
              color="inherit"
              onClick={() =>
                setSnackbarData({ severity: "success", message: "" })
              }
            >
              <Close fontSize="small" />
            </IconButton>
          }
        >
          {snackbarData?.message}
        </Alert>
      </Snackbar>
    </Paper>
  )
}

export default SaLockerForm
